import React, { useCallback, useMemo } from "react";
import { Box, NativeSelect, Text, HStack, Flex, Field } from "@chakra-ui/react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { InvoiceFromOcrDto } from "@/api/invoiceApi";
import { LuPlus } from "react-icons/lu";
import { Button, ErrorMessage } from "@digital-services-gard-as/anchor-react";
import { BankAccountDto, InvoiceFormData } from "@/types";
import { BankAccountAlert } from "./BankAccountAlert";
import { toOcrBankAccount } from "./utils";
import { BankAccountPrefilledBadge } from "./PrefilledBadge";

type BankAccountSelectProps = {
    bankAccounts?: BankAccountDto[];
    ocrResponse?: InvoiceFromOcrDto;
    onOpenBankDetails?: () => void;
    missingBankAccount: string | undefined;
};

export const BankAccountSelect = React.memo(function BankAccountSelect({
    bankAccounts,
    ocrResponse,
    onOpenBankDetails,
    missingBankAccount
}: BankAccountSelectProps) {
    const { t } = useTranslation(["invoices"]);
    const {
        watch,
        setValue,
        formState: { errors }
    } = useFormContext<InvoiceFormData>();

    const bankAccount = watch("bankAccount");

    const selectValue = useMemo(
        () => (bankAccount ? `${bankAccount.accountNumber} - ${bankAccount.currency}` : ""),
        [bankAccount]
    );

    const ocrBankAccount = toOcrBankAccount(ocrResponse);

    const handleBankAccountChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const [accountNumber, currency] = e.target.value.split(" - ");
            const selected = bankAccounts?.find(b => b.accountNumber === accountNumber && b.currency === currency);
            if (selected) {
                setValue("bankAccount", selected, { shouldDirty: true, shouldValidate: true });
            }
        },
        [bankAccounts, setValue]
    );

    return (
        <>
            <BankAccountAlert bankAccounts={bankAccounts} ocrResponse={ocrResponse} />
            <HStack align="flex-start" gap={6}>
                <Field.Root flex={1} invalid={!!errors?.bankAccount?.message || !!missingBankAccount}>
                    <Flex gap="0.5rem" alignItems="center">
                        <Field.Label m={0} whiteSpace="nowrap">
                            {t("invoices:createInvoice.generalInfo.bankAccount")}
                        </Field.Label>
                        <BankAccountPrefilledBadge
                            bankAccounts={bankAccounts}
                            ocrBankAccount={ocrBankAccount}
                            bankAccount={bankAccount}
                        />
                    </Flex>
                    <NativeSelect.Root>
                        <NativeSelect.Field
                            id="bankAccount"
                            value={selectValue ?? ""}
                            onChange={handleBankAccountChange}
                            placeholder={t("invoices:createInvoice.generalInfo.bankAccountPlaceholder")}
                        >
                            <option value="" disabled hidden>
                                {t("invoices:createInvoice.generalInfo.bankAccountPlaceholder")}
                            </option>
                            {bankAccounts?.map(b => {
                                const value = `${b.accountNumber} - ${b.currency}`;
                                return (
                                    <option key={value} value={value}>
                                        {value}
                                    </option>
                                );
                            })}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                    </NativeSelect.Root>
                    <ErrorMessage size="sm" data-testid="missingBankAccountError">
                        {errors?.bankAccount?.message || missingBankAccount}
                    </ErrorMessage>
                </Field.Root>
                <Field.Root w="auto">
                    <Field.Label m={0} whiteSpace="nowrap">
                        {t("invoices:createInvoice.generalInfo.currency")}
                    </Field.Label>

                    <Box h="2.5rem" display="flex" alignItems="center" justifyContent="flex-start" pl={1}>
                        <Text>{bankAccount?.currency || "—"}</Text>
                    </Box>
                </Field.Root>
            </HStack>
            <Button
                type="button"
                id="addBankAccountBtn"
                variant="secondary"
                onClick={onOpenBankDetails}
                icon={<LuPlus />}
            >
                {t("invoices:createInvoice.generalInfo.addBankAccountBtn")}
            </Button>
        </>
    );
});
